import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Search, RefreshCw} from "lucide-react";
import { selectModuleTabs } from "../../../redux/selectors/manageTeamTabSelector";
import { addTab, removeTab, setActiveTab } from "../../../redux/slices/manageTeamSlice";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import TeamTable from "./TabContent/TeamTable";
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import OrgInvitesTab from "./TabContent/OrgInvitesTab";
import PreferedPartners from "./TabContent/PreferredPartners";
import AddChildOrganizationTab from "./TabContent/AddChildOrganizationTab";
import RoleManagement from "./TabContent/RoleManagement";
import BulkUploadTab from "./TabContent/BulkUpload";
import { usePrivilege } from "../../../guard/usePrivilege";
import ViewLocation from "./TabContent/ViewTab/ViewLocation";
import CreateLocationTab from "./TabContent/CreateLocationTab";
import ViewUserProfile from "./TabContent/ViewTab/ViewUserProfile";
import ViewPreferredPartnerDetails from "./TabContent/ViewTab/ViewPreferredPartnerDetails";
import Cookies from "js-cookie";
import { fetchAllRoles,searchUsers,searchOrganizations } from "./api";
import TeamDropdownContent from "./TeamDropdownContent"; // ✅ new nested filter dropdown
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import SystemUsersTab from "../SystemUsers/SystemUsersTab";


const ManageTeamDetails = () => {
  const dispatch = useDispatch();
  const { tabs, activeTab, defaultTabsCount } = useSelector(selectModuleTabs("manageTeam"));
  const { hasPrivilege } = usePrivilege();
  // addTeamMember:ADD was granted broadly (Admin Role, SHIPPER VIEW, Carrier View, Team Admin);
  // adding/managing team members is admin-only (LGX-1006 follow-up).
  const canAddTeamMember = hasPrivilege("manageTeam:ADMIN");

  // ✅ NEW: Filter & dropdown state
  const [filters, setFilters] = useState({
    offices: [],
    roles: [],
    status: "",
    sortBy: "",
  });
  const [searchResults, setSearchResults] = useState(null);
  const [offices, setOffices] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [reloading, setReloading] = useState(false);


  useEffect(() => {
    // Decode office codes from cookie
    const cookieVal = Cookies.get("officeCodes");
    if (cookieVal) {
      const decoded = decodeURIComponent(cookieVal).split(",");
      setOffices(decoded);
    }

    // Fetch roles using auth token
    const token = Cookies.get("authToken");
    fetchAllRoles(token)
      .then((res) => setRoles(res.data || []))
      .catch((err) => console.error("Failed to fetch roles", err));
  }, []);
  useEffect(() => {
  setSearchTerm("");
  setSearchResults(null);
}, [activeTab]);

useEffect(() => {
  if (!searchTerm.trim()) {
    setSearchResults(null);
  }
}, [searchTerm]);

  const handleCreateTeam = () => {
    if (!canAddTeamMember) {
            return;
    }
    const id = `create-${Date.now()}`;
    dispatch(
      addTab({
        id,
        name: "Add Team Member",
        contentType: "addTeam",
        isDynamic: true,
        createdAt: Date.now(),
      })
    );
    dispatch(setActiveTab({ index: tabs.length }));
  };

  const handleRemoveTab = (index) => {
    if (index < defaultTabsCount) {
      alert("Default tabs cannot be removed.");
      return;
    }
    dispatch(removeTab({ tabIndex: index, defaultTabsCount }));
  };

  const handleApplyFilters = async (filters) => {
    // 🧩 If filters are cleared → reset to default
    if (!filters) {
      setSearchResults(null); // ✅ TeamTable will now fallback to fetchTeamMembers()
      notify.info({
        title: "Filters Cleared",
        message: "Showing all team members.",
      });
      return;
    }

    try {
      const res = await searchUsers(filters);
      
      setSearchResults(res.data.content || res.data);

      notify.success({
        title: "Search Completed",
        message: `Found ${res.data.content?.length || res.data.length || 0} matching team members.`,
      });
    } catch (err) {
      console.error("❌ Search failed:", err);
      notify.error({
        title: "Search Failed",
        message: err.response?.data?.message || "Unable to fetch search results. Please try again.",
      });
    }
  };
  const currentTab = tabs[activeTab];
  const tabLabels = {
  "teamMembers": { placeholder: "Search Team", label: "Team Members" },
  "org-invites": { placeholder: "Search Invites", label: "Organization Invites" },
  "pref-partners": { placeholder: "Search Partners", label: "Preferred Partners" },
  "child-org": { placeholder: "Search Offices", label: "Office Management" },
  "role-mgmt": { placeholder: "Search Roles", label: "Role Management" },
  "location-mgmt": { placeholder: "Search Locations", label: "Location Management" },
  "system-users": { placeholder: "Search Organizations", label: "System Users" },
};
const currentLabels = tabLabels[currentTab?.id] || { placeholder: "Search", label: "" };


  const handleSearch = async () => {
  if (!searchTerm.trim()) return;
  try {
    let res;
    if (currentTab?.id === "pref-partners") {
      res = await searchOrganizations(searchTerm);
      setSearchResults(res.data.content || []);
      notify.success({
        title: "Search Completed",
        message: `Found ${res.data.content?.length || 0} matching partners.`,
      });
    } else if (currentTab?.id === "teamMembers") {
      res = await searchUsers({ searchTerm });
      setSearchResults(res.data.content || res.data);
      notify.success({
        title: "Search Completed",
        message: `Found ${res.data.content?.length || res.data.length || 0} matching team members.`,
      });
    } else {
      return;
    }
  } catch (err) {
    console.error("Search failed:", err);
    notify.error({ title: "Search Failed", message: "Unable to fetch search results." });
  }
};






  return (
    <div className="p-fluid-1 shadow-md flex flex-col flex-1 h-full min-h-0 overflow-hidden">
      {/* === Top Action/Filter Bar === */}
      <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1">
        {/* === Left side: Filter + Search + Label === */}
        <div className="flex flex-wrap items-center gap-2">
          {/* ✅ Reusable Filter Button with nested dropdown */}
          <FilterButton
            text="Filter by"
            contentComponent={
              <TeamDropdownContent
                filters={filters}
                setFilters={setFilters}
                offices={offices}
                roles={roles}
                onApply={handleApplyFilters}
              />
            }
          />

          {/* === Search Bar === */}
          <div className="relative flex items-center border border-gray-300 dark:border-acs-dark-border bg-white dark:bg-[#2A2D35] rounded-full px-3 py-1.5 w-80">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder={
                currentLabels.placeholder
              }
              className="w-full outline-none bg-transparent text-fluid-sm text-gray-800 dark:text-acs-dark-text placeholder:text-gray-400 dark:placeholder:text-acs-dark-subtext"
            />
            <button
              onClick={handleSearch}
              className="absolute right-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-acs-dark-text"
            >
              <Search size={18} strokeWidth={2} />
            </button>
          </div>

          <span className="text-gray-500 text-fluid-sm">{currentLabels.label}</span>
          <button
                      onClick={() => {
                        setReloading(true);           // spin the icon briefly
                        setTimeout(() => window.location.reload(), 150); // tiny delay for visual feedback
                      }}
                      className="p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#223688]/30 transition"
                      title="Reload page"
                      aria-label="Reload page"
                    >
                      <RefreshCw
                        size={16}
                        className={`text-gray-500 text-acs-light-text dark:text-acs-dark-text ${reloading ? "animate-spin" : "hover:text-gray-700"}`}
                      />
                    </button>
        </div>

      </div>

      {/* === Tab Content === */}
      <div className="flex-1 mt-fluid-2 min-h-0 flex flex-col scrollbar-hide">
        {tabs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 text-lg font-semibold">
            No tabs available or you do not have access.
          </div>
        ) : (
          <Tabs
            tabs={tabs.map((tab) => tab.name)}
            tabContents={tabs.map((tab) => {
              if (tab.contentType === "addTeam") {
                return (
                  <BulkUploadTab
                    key={tab.id}
                    onSuccess={(msg) => {
                      const tabIndex = tabs.findIndex((t) => t.id === tab.id);
                      if (tabIndex >= defaultTabsCount) handleRemoveTab(tabIndex);
                      dispatch(setActiveTab({ index: 0 }));
                    }}
                    onCancel={() =>
                      handleRemoveTab(tabs.findIndex((t) => t.id === tab.id))
                    }
                  />
                );
              }
              if (tab.id === "teamMembers")
                return (
                  <TeamTable
                    key="teamMembers"
                    data={searchResults}
                    onAddTeamMember={handleCreateTeam}
                  />
                );
              if (tab.id === "org-invites") return <OrgInvitesTab key="org-invites" searchTerm={searchTerm} />;
              if (tab.id === "pref-partners")
                return <PreferedPartners key="pref-partners" data={searchResults} />;
              if (tab.id === "child-org") return <AddChildOrganizationTab key="child-org" searchTerm={searchTerm} />;
              if (tab.id === "role-mgmt") return <RoleManagement key="role-mgmt" />;
              if (tab.id === "system-users") return <SystemUsersTab key="system-users" />;
              if (tab.id === "bulk-upload") return <BulkUploadTab key="bulk-upload" />;
              if (tab.contentType === "viewPreferredPartnerDetails")
                return (
                  <ViewPreferredPartnerDetails
                    key={tab.id}
                    orgData={tab.data}
                    onClose={() => handleRemoveTab(tabs.findIndex((t) => t.id === tab.id))}
                  />
                );
              if (tab.contentType === "addOffice")
                return (
                  <ViewPreferredPartnerDetails
                    key={tab.id}
                    isNew
                    parentOfficeCode={tab.data?.parentOfficeCode}
                    onClose={() => handleRemoveTab(tabs.findIndex((t) => t.id === tab.id))}
                    onSuccess={(newOfficeCode) => {
                      notify.success("Office added.");
                      // officeCodes is a login-time snapshot cookie (set in LoginPage.js/VerifyOTP.js)
                      // that every office/warehouse-scoped fetch reads from (see api.js's
                      // getOfficeCodes) -- without appending here, a just-created office is
                      // invisible to those fetches (e.g. "Add Warehouse" wrongly reports the new
                      // office has no address on file) until the user logs out and back in.
                      if (newOfficeCode) {
                        const existing = Cookies.get("officeCodes");
                        const codes = existing ? existing.split(",").filter(Boolean) : [];
                        if (!codes.includes(newOfficeCode)) {
                          codes.push(newOfficeCode);
                          Cookies.set("officeCodes", codes.join(","), {
                            domain: process.env.REACT_APP_COOKIE_DOMAIN,
                            secure: process.env.REACT_APP_COOKIE_SECURE === "true",
                            sameSite: "Lax",
                          });
                        }
                      }
                      window.dispatchEvent(new Event("organization:refresh"));
                      window.dispatchEvent(new Event("location:refresh"));
                    }}
                  />
                );
              if (tab.contentType === "viewLocation")
                return (
                  <ViewLocation
                    key={tab.id}
                    location={tab.data}
                  />
                );
              if (tab.contentType === "viewUserProfile")
                return <ViewUserProfile key={tab.id} userData={tab.data} />;
              if (tab.contentType === "addLocation")
                return (
                  <ViewLocation
                    key={tab.id}
                    isNew
                    parentRef={tab.data?.locationRef}
                    officeCode={tab.data?.officeCode}
                    onClose={() =>
                      handleRemoveTab(tabs.findIndex((t) => t.id === tab.id))
                    }
                    onSuccess={() => {
                      notify.success("Warehouse added.");
                      window.dispatchEvent(new Event("location:refresh"));
                    }}
                  />
                );
              if (tab.contentType === "createLocation")
                return (
                  <CreateLocationTab
                    key={tab.id}
                    onClose={() =>
                      handleRemoveTab(tabs.findIndex((t) => t.id === tab.id))
                    }
                    onSuccess={() => notify.success("Location created")}
                  />
                );
              return <div key={tab.id}>Access Denied</div>;
            })}
            activeTab={activeTab}
            onTabChange={(index) => dispatch(setActiveTab({ index }))}
            isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
            onRemoveTab={handleRemoveTab}
          />
        )}
      </div>
    </div>
  );
};

export default ManageTeamDetails;